/**
 * TanStack Query hooks for the Resource Plan M3 API — Level 1 estimating, the back-solve
 * and the Planned Pod.
 *
 * Same conventions as use-resource-plans.ts: every mutation invalidates by
 * `resourcePlanKeys.all`, owns its own toast, and writes no optimistic cache entries
 * (.claude/rules/frontend.md).
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY THE PLANNED POD, THE MIX AND THE RAMP HAVE NO KEYS OF THEIR OWN
 * ──────────────────────────────────────────────────────────────────────────────
 * They are part of the plan detail, and every M3 mutation returns the whole re-shaped
 * `ResourcePlanDetailView`. So composing a Pod refreshes the grid's Position links, the
 * summary strip's hours and the margin badge from the same round trip — which is what
 * keeps RP-7.3 ("the number on screen is never stale") true after an M3 edit as well as
 * an M2 one. Only the two reads that are NOT part of a plan get keys: the Workstream
 * Template library, and the Shape picker.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * THE SOLVE IS A MUTATION THAT WRITES NOTHING
 * ──────────────────────────────────────────────────────────────────────────────
 * `usePlanSolve` POSTs and returns a proposal without touching the database (RP-5.1:
 * "Nothing auto-applies"). It is a `useMutation` rather than a `useQuery` because it is
 * triggered by a button and its result is a one-shot answer to a question the planner
 * just asked — caching it under a key would make a stale proposal reappear next time the
 * panel opened. It therefore invalidates nothing, and stays silent on success: the panel
 * renders the diff, and a toast on top of it would be noise.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { planShapeKeys, resourcePlanKeys, workstreamTemplateKeys } from "@/lib/query";
import type {
  PlanSolveProposalView,
  ResourcePlanDetailView,
  WorkstreamTemplateView,
} from "@/schemas/resource-plans";

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

async function send<T>(url: string, method: string, body?: unknown): Promise<T> {
  return readJsonOrThrow<T>(
    await fetch(url, {
      method,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
}

/** Invalidate every plan read. Broad on purpose — an M3 edit moves totals everywhere. */
function useInvalidatePlans() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: resourcePlanKeys.all });
    qc.invalidateQueries({ queryKey: ["dashboard-solutions"] });
    qc.invalidateQueries({ queryKey: ["accounts"] });
  };
}

// ── Epic 13 — Workstream Templates ────────────────────────────────────────────

export function useWorkstreamTemplates(includeSuperseded = false, enabled = true) {
  return useQuery({
    queryKey: workstreamTemplateKeys.list(includeSuperseded),
    queryFn: async () => {
      const { templates } = await readJsonOrThrow<{ templates: WorkstreamTemplateView[] }>(
        await fetch(
          `/api/workstream-templates${includeSuperseded ? "?includeSuperseded=true" : ""}`,
        ),
      );
      return templates;
    },
    enabled,
  });
}

export interface SaveWorkstreamTemplateInput {
  type: string;
  description?: string | null;
  sizeSHours: string;
  sizeMHours: string;
  sizeLHours: string;
  sizeXlHours: string;
  lines: Array<{ laborCategoryId: string; mixPercent: string }>;
}

export function useSaveWorkstreamTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, ...body }: SaveWorkstreamTemplateInput) =>
      send<WorkstreamTemplateView>(`/api/workstream-templates/${type}`, "PUT", body),
    onSuccess: (template) => {
      qc.invalidateQueries({ queryKey: workstreamTemplateKeys.all });
      // Existing Workstreams keep the version they were sized from (RP-13.1), but any
      // open plan's mix side-by-side reads the type's ACTIVE template, so refresh plans.
      qc.invalidateQueries({ queryKey: resourcePlanKeys.all });
      toast.success(`Saved ${template.type} template v${template.version}`);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to save the template"),
  });
}

export function useActivateWorkstreamTemplateVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) =>
      send<WorkstreamTemplateView>(
        `/api/workstream-template-versions/${templateId}/activate`,
        "POST",
      ),
    onSuccess: (template) => {
      qc.invalidateQueries({ queryKey: workstreamTemplateKeys.all });
      qc.invalidateQueries({ queryKey: resourcePlanKeys.all });
      toast.success(`${template.type} is back on v${template.version}`);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to reinstate that version"),
  });
}

// ── Epic 3 — sizing and the mix ───────────────────────────────────────────────

export interface SizeWorkstreamInput {
  workstreamId: string;
  totalHours?: string;
  sizePreset?: "S" | "M" | "L" | "XL";
  replaceExisting?: boolean;
}

export function useSizeWorkstream() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ workstreamId, ...body }: SizeWorkstreamInput) =>
      send<ResourcePlanDetailView>(`/api/workstreams/${workstreamId}/size`, "POST", body),
    onSuccess: () => {
      invalidate();
      toast.success("Workstream sized — every cell is editable");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to size the Workstream"),
  });
}

export interface SetWorkstreamMixInput {
  workstreamId: string;
  /** null CLEARS the override and returns the Workstream to its template's mix. */
  lines: Array<{ laborCategoryId: string; mixPercent: string }> | null;
  reason?: string;
}

export function useSetWorkstreamMix() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ workstreamId, ...body }: SetWorkstreamMixInput) =>
      send<ResourcePlanDetailView>(`/api/workstreams/${workstreamId}/mix`, "PUT", body),
    onSuccess: (_plan, input) => {
      invalidate();
      toast.success(
        input.lines === null ? "Back to the template's mix" : "Mix overridden for this Workstream",
      );
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to save the mix"),
  });
}

// ── RP-4.3 — ramp ─────────────────────────────────────────────────────────────

export interface SetPlanRampInput {
  planId: string;
  months: Array<{ month: string; factor: string }>;
}

export function useSetPlanRamp() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ planId, months }: SetPlanRampInput) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/ramp`, "PUT", { months }),
    onSuccess: () => invalidate(),
    // No toast: the ramp row is edited in place and the totals move immediately, which is
    // its own confirmation. A toast per keystroke-committed factor would be noise.
    onError: (err: Error) => toast.error(err.message ?? "Failed to set the ramp"),
  });
}

export function useSetPlanLineLock() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ lineId, locked }: { lineId: string; locked: boolean }) =>
      send<ResourcePlanDetailView>(`/api/plan-lines/${lineId}/lock`, "PUT", { locked }),
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast.error(err.message ?? "Failed to lock the line"),
  });
}

// ── Epic 5 — the solvers ──────────────────────────────────────────────────────

export type PlanSolveRequest =
  | { kind: "budget"; targetRevenue: string }
  | { kind: "hours"; targetHours: string }
  | { kind: "targetMargin"; targetMarginPercent: string };

/** Compute a proposal. Writes nothing (RP-5.1) — see the header. */
export function usePlanSolve(planId: string) {
  return useMutation({
    mutationFn: (request: PlanSolveRequest) =>
      send<PlanSolveProposalView>(`/api/plans/${planId}/solve`, "POST", request),
    onError: (err: Error) => toast.error(err.message ?? "Failed to solve"),
  });
}

/** Apply a proposal. Re-derives from the same inputs server-side. */
export function useApplyPlanSolve(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (request: PlanSolveRequest) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/solve/apply`, "POST", request),
    onSuccess: (plan) => {
      invalidate();
      toast.success(`Applied — ${plan.totalHours} hours, ${plan.totalRevenue}`);
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to apply the solve"),
  });
}

// ── Epic 2 — the Planned Pod ──────────────────────────────────────────────────

export interface PlanShapeOption {
  id: string;
  name: string;
  composition: string;
  isTierDefault: boolean;
  disciplineCodes: string[];
}

export function usePlanShapes(planId: string, enabled = true) {
  return useQuery({
    queryKey: planShapeKeys.forPlan(planId),
    queryFn: async () => {
      const { shapes } = await readJsonOrThrow<{ shapes: PlanShapeOption[] }>(
        await fetch(`/api/plans/${planId}/pod-shapes`),
      );
      return shapes;
    },
    enabled,
  });
}

export function useComposePlannedPod(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: { shapeId: string | null; createLines?: boolean }) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/planned-pod`, "PUT", input),
    onSuccess: (plan) => {
      invalidate();
      toast.success(
        plan.plannedPod?.shapeSource === "Custom"
          ? "Custom Planned Pod started — add Positions as you need them"
          : `Planned Pod from ${plan.plannedPod?.shapeName ?? "the Shape"}`,
      );
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to compose the Planned Pod"),
  });
}

export function useClearPlannedPod(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: () => send<ResourcePlanDetailView>(`/api/plans/${planId}/planned-pod`, "DELETE"),
    onSuccess: () => {
      invalidate();
      toast.success("Planned Pod cleared — Plan Lines and their hours are untouched");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to clear the Planned Pod"),
  });
}

export function useCreatePlannedPodPosition(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: {
      positionCode: string;
      expectedDisciplineId: string;
      laborCategoryId?: string | null;
    }) => send<ResourcePlanDetailView>(`/api/plans/${planId}/planned-pod/positions`, "POST", input),
    onSuccess: () => {
      invalidate();
      toast.success("Position added");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to add the Position"),
  });
}

export function useUpdatePlannedPodPosition() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({
      positionId,
      ...patch
    }: {
      positionId: string;
      expectedDisciplineId?: string;
      laborCategoryId?: string | null;
    }) => send<ResourcePlanDetailView>(`/api/planned-pod-positions/${positionId}`, "PATCH", patch),
    onSuccess: () => {
      invalidate();
      toast.success("Position updated");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the Position"),
  });
}

export function useDeletePlannedPodPosition() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (positionId: string) =>
      send<ResourcePlanDetailView>(`/api/planned-pod-positions/${positionId}`, "DELETE"),
    onSuccess: () => {
      invalidate();
      toast.success("Position removed — its Plan Lines kept their hours");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to remove the Position"),
  });
}

export function useSetPlanLinePosition() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({
      lineId,
      plannedPodPositionId,
    }: {
      lineId: string;
      plannedPodPositionId: string | null;
    }) =>
      send<ResourcePlanDetailView>(`/api/plan-lines/${lineId}/planned-pod-position`, "PUT", {
        plannedPodPositionId,
      }),
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast.error(err.message ?? "Failed to link the Plan Line"),
  });
}
